import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { omit } from 'lodash'
import { AppsRoutes, getRoute } from '@wellesley/router'
import { useCurrentUser } from '@wellesley/user'
import { getUserNameWithDomain } from 'helpers/getUserNameWithDomain'
import { EventScope } from './Event.types'
import { eventsSlice } from '../slices'

const USER_EVENTS_PREFIX = '/@/:username/events'
const GROUP_EVENTS_PREFIX = '/g/:groupname/events'

// User
export const USER_EVENTS_ROUTE = USER_EVENTS_PREFIX
export const EVENTS_SETTINGS_ROUTE = `${USER_EVENTS_PREFIX}/settings`
export const EVENTS_DISCOVER_ROUTE = `${USER_EVENTS_PREFIX}/discover`
export const EVENTS_NOTIFICATIONS_ROUTE = `${USER_EVENTS_PREFIX}/notifications`
export const USER_EVENT_ROUTE = `${USER_EVENTS_PREFIX}/:eventId`

// Group
export const GROUP_EVENTS_ROUTE = GROUP_EVENTS_PREFIX
export const GROUP_EVENTS_SETTINGS_ROUTE = `${GROUP_EVENTS_PREFIX}/settings`
export const GROUP_EVENT_ROUTE = `${GROUP_EVENTS_PREFIX}/:eventId`

const routes: Record<'user' | 'group', Record<string, AppsRoutes>> = {
  user: {
    event: USER_EVENT_ROUTE,
    list: USER_EVENTS_ROUTE,
    settings: EVENTS_SETTINGS_ROUTE,
    discover: EVENTS_DISCOVER_ROUTE,
    notifications: EVENTS_NOTIFICATIONS_ROUTE,
  },
  group: {
    event: GROUP_EVENT_ROUTE,
    list: GROUP_EVENTS_ROUTE,
    settings: GROUP_EVENTS_SETTINGS_ROUTE,
  },
}

type PathToProps =
  | {
      to: 'event'
      useFromScope?: ('group' | 'user')[]
      routeProps: {
        eventId: string
        username?: string
        groupname?: string
      }
    }
  | {
      to: 'list' | 'settings'
      useFromScope?: ('group' | 'user')[]
      routeProps?: null
    }
  | {
      to: 'discover' | 'notifications'
      useFromScope?: null
      routeProps?: null
    }

export const useEventRoutes = () => {
  const { scope, scopeProps } = useSelector(eventsSlice.selectors.selectConfigs)

  const { user: currentUser } = useCurrentUser()
  const currentUsername = currentUser ? getUserNameWithDomain(currentUser) : ''

  const pathTo = useCallback(
    (args: PathToProps) => {
      const { to, useFromScope, routeProps } = args

      if (useFromScope?.includes('group') && scope === EventScope.MY_GROUP) {
        return getRoute(routes.group[to], {
          groupname: scopeProps.group.groupName,
          ...(routeProps && { ...omit(routeProps, 'username') }),
        })
      }

      let username = currentUsername

      if (useFromScope?.includes('user') && scope === EventScope.USER) {
        username = scopeProps.username
      }

      return getRoute(routes.user[to], {
        username,
        ...(routeProps && { ...omit(routeProps, 'groupname') }),
      })
    },
    [scope, scopeProps, currentUsername],
  )

  return pathTo
}
